import React, { useState, useEffect } from "react"
import { Loader2, ChevronDown } from "lucide-react"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "src/components/ui/sheet"
import { Input } from "src/components/ui/input"
import { Button } from "src/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "src/components/ui/dropdown-menu"
import { FormItem, FormLabel, FormControl } from "src/components/ui/form"
import { useToast } from "src/components/ui/toast"
import { createUnit, updateUnit } from "../api/projectsApi"

const STATUSES = [
  { value: "available", label: "Available" },
  { value: "blocked", label: "Blocked" },
  { value: "booked", label: "Booked" },
  { value: "sold", label: "Sold" },
]

const EMPTY = {
  tower_id: "", unit_number: "", unit_type: "", status: "available",
  bedrooms: "", bathrooms: "", floor_number: "", facing: "",
  carpet_area: "", builtup_area: "", base_price: "", price_per_sqft: "",
}

// Drawer to add OR edit a unit. Pass `unit` to edit; omit to create.
export function UnitFormDrawer({ open, projectId, unit, towers = [], onClose, onSuccess }) {
  const { toast } = useToast()
  const [form, setForm] = useState(EMPTY)
  const [pending, setPending] = useState(false)
  const isEdit = !!unit

  useEffect(() => {
    if (!open) return
    if (unit) {
      const str = (v) => (v == null ? "" : String(v))
      setForm({
        tower_id: str(unit.tower_id), unit_number: str(unit.unit_number), unit_type: str(unit.unit_type),
        status: unit.status || "available", bedrooms: str(unit.bedrooms), bathrooms: str(unit.bathrooms),
        floor_number: str(unit.floor_number), facing: str(unit.facing), carpet_area: str(unit.carpet_area),
        builtup_area: str(unit.builtup_area), base_price: str(unit.base_price), price_per_sqft: str(unit.price_per_sqft),
      })
    } else {
      setForm(EMPTY)
    }
  }, [open, unit])

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }))
  }

  const towerLabel = towers.find((t) => String(t.id) === String(form.tower_id))?.name || "No tower"
  const statusLabel = STATUSES.find((s) => s.value === form.status)?.label || "Available"

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.unit_number.trim()) return
    setPending(true)
    try {
      const num = (v) => (v === "" ? undefined : Number(v))
      const payload = {
        tower_id: form.tower_id || undefined,
        unit_number: form.unit_number.trim(),
        unit_type: form.unit_type.trim() || undefined,
        bedrooms: num(form.bedrooms),
        bathrooms: num(form.bathrooms),
        floor_number: num(form.floor_number),
        facing: form.facing.trim() || undefined,
        carpet_area: num(form.carpet_area),
        builtup_area: num(form.builtup_area),
        base_price: num(form.base_price),
        price_per_sqft: num(form.price_per_sqft),
      }
      // Status only set at creation; changes go through the status state machine.
      if (!isEdit) payload.status = form.status

      if (isEdit) {
        await updateUnit(projectId, unit.id, payload)
        toast({ title: "Unit updated", description: payload.unit_number })
      } else {
        await createUnit(projectId, payload)
        toast({ title: "Unit added", description: payload.unit_number })
      }
      onSuccess?.()
      onClose()
    } catch (err) {
      const msg = err.response?.data?.errors?.[0] || `Could not ${isEdit ? "update" : "add"} unit`
      toast({ variant: "destructive", title: "Error", description: msg })
    } finally {
      setPending(false)
    }
  }

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="flex flex-col p-0 sm:max-w-[480px]">
        <SheetHeader>
          <SheetTitle>{isEdit ? "Edit unit" : "Add unit"}</SheetTitle>
          <SheetDescription>
            {isEdit
              ? "Update unit details. To change availability, use the status action on the unit."
              : "Add a sellable unit (flat / shop / plot) to this project."}
          </SheetDescription>
        </SheetHeader>
        <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
            <div className="grid grid-cols-2 gap-3">
              <FormItem>
                <FormLabel>Unit number</FormLabel>
                <FormControl>
                  <Input placeholder="e.g. A-101" value={form.unit_number} onChange={(e) => set("unit_number", e.target.value)} autoFocus />
                </FormControl>
              </FormItem>
              <FormItem>
                <FormLabel>Tower</FormLabel>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button type="button" variant="outline" className="w-full justify-between font-normal text-slate-700">
                      {towerLabel}
                      <ChevronDown className="h-4 w-4 opacity-50" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width]">
                    <DropdownMenuRadioGroup value={String(form.tower_id)} onValueChange={(v) => set("tower_id", v === "none" ? "" : v)}>
                      <DropdownMenuRadioItem value="none">No tower</DropdownMenuRadioItem>
                      {towers.map((t) => (
                        <DropdownMenuRadioItem key={t.id} value={String(t.id)}>{t.name}</DropdownMenuRadioItem>
                      ))}
                    </DropdownMenuRadioGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              </FormItem>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <FormItem>
                <FormLabel>Unit type</FormLabel>
                <FormControl>
                  <Input placeholder="e.g. 2BHK" value={form.unit_type} onChange={(e) => set("unit_type", e.target.value)} />
                </FormControl>
              </FormItem>
              {!isEdit && (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button type="button" variant="outline" className="w-full justify-between font-normal text-slate-700">
                        {statusLabel}
                        <ChevronDown className="h-4 w-4 opacity-50" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width]">
                      <DropdownMenuRadioGroup value={form.status} onValueChange={(v) => set("status", v)}>
                        {STATUSES.map((s) => (
                          <DropdownMenuRadioItem key={s.value} value={s.value}>{s.label}</DropdownMenuRadioItem>
                        ))}
                      </DropdownMenuRadioGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </FormItem>
              )}
            </div>

            <div className="grid grid-cols-3 gap-3">
              <FormItem>
                <FormLabel>Bedrooms</FormLabel>
                <FormControl><Input type="number" min="0" value={form.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} /></FormControl>
              </FormItem>
              <FormItem>
                <FormLabel>Bathrooms</FormLabel>
                <FormControl><Input type="number" min="0" value={form.bathrooms} onChange={(e) => set("bathrooms", e.target.value)} /></FormControl>
              </FormItem>
              <FormItem>
                <FormLabel>Floor</FormLabel>
                <FormControl><Input type="number" value={form.floor_number} onChange={(e) => set("floor_number", e.target.value)} /></FormControl>
              </FormItem>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <FormItem>
                <FormLabel>Carpet area (sqft)</FormLabel>
                <FormControl><Input type="number" min="0" value={form.carpet_area} onChange={(e) => set("carpet_area", e.target.value)} /></FormControl>
              </FormItem>
              <FormItem>
                <FormLabel>Built-up area (sqft)</FormLabel>
                <FormControl><Input type="number" min="0" value={form.builtup_area} onChange={(e) => set("builtup_area", e.target.value)} /></FormControl>
              </FormItem>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <FormItem>
                <FormLabel>Base price (₹)</FormLabel>
                <FormControl><Input type="number" min="0" value={form.base_price} onChange={(e) => set("base_price", e.target.value)} /></FormControl>
              </FormItem>
              <FormItem>
                <FormLabel>Price / sqft (₹)</FormLabel>
                <FormControl><Input type="number" min="0" value={form.price_per_sqft} onChange={(e) => set("price_per_sqft", e.target.value)} /></FormControl>
              </FormItem>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-6 py-4">
            <Button type="button" variant="ghost" onClick={onClose} disabled={pending}>Cancel</Button>
            <Button type="submit" disabled={pending || !form.unit_number.trim()} className="gap-2">
              {pending && <Loader2 className="h-4 w-4 animate-spin" />}
              {isEdit ? "Save changes" : "Add unit"}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}
